import React from 'react';
import { format } from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';
import { Printer, Trash2, Ticket, Ban } from 'lucide-react';

const ListaVentas = ({ ventasFiltradas, handleImprimirEntrada, handleAnularVenta }) => {
  const totalActivas = ventasFiltradas.reduce(
    (sum, venta) => (venta.anulado ? sum : sum + Number(venta.totalVentas || 0)),
    0
  );

  return (
    <div className="bg-blanco-puro bg-opacity-10 rounded-xl p-6 shadow-lg">
      <h2 className="text-2xl font-bold text-amarillo-dorado mb-4 flex items-center">
        <Ticket className="mr-2" />
        Ventas Registradas Hoy
      </h2>

      <div className="mb-4">
        <div className="grid grid-cols-12 gap-1 font-semibold text-gris-piedra pb-2 mb-2 border-b border-gris-piedra border-opacity-30 text-sm whitespace-nowrap">
          <div className="col-span-1 px-1">Ticket</div>
          <div className="col-span-1 px-1">Hora</div>
          <div className="col-span-2 px-1">Usuario</div>
          <div className="col-span-1 px-1 text-center">Entradas</div>
          <div className="col-span-2 px-1 text-right">Total</div>
          <div className="col-span-2 px-1">Medios</div>
          <div className="col-span-1 px-1 text-right">Monto 1</div>
          <div className="col-span-1 px-1 text-right">Monto 2</div>
          <div className="col-span-1 px-1">Acciones</div>
        </div>

        <AnimatePresence>
          {ventasFiltradas.map((venta) => {
            const esAnulada = Boolean(venta.anulado);
            const fechaAnulacion = venta.fechaAnulacion ? new Date(venta.fechaAnulacion) : null;
            const tooltipAnulacion = fechaAnulacion
              ? `Anulada el ${format(fechaAnulacion, 'dd/MM/yyyy HH:mm')}`
              : 'Venta anulada';
            const diasDetalle = Array.isArray(venta.diasDetalle)
              ? venta.diasDetalle
              : [];
            const diasEstadia =
              diasDetalle.length || venta.diasEstadia || venta.dias_estadia || 1;
            let fechaValidez = null;
            const extrasVenta = Array.isArray(venta.extras) ? venta.extras : [];
            const extrasSubtotal = extrasVenta.reduce((sum, extra) => sum + Number(extra.total || 0), 0);
            const extrasTexto = extrasVenta.map((extra) => {
              const nombreExtra = extra.extra?.nombre || extra.nombre || `Extra ${extra.id}`;
              const cantidadExtra = Number(extra.cantidad || 0);
              return `${nombreExtra} x${cantidadExtra}`;
            }).join(', ');

            if (venta.validoHasta || venta.valido_hasta) {
              fechaValidez = new Date(venta.validoHasta || venta.valido_hasta);
            } else if (venta.createdAt) {
              fechaValidez = new Date(venta.createdAt);
              fechaValidez.setDate(fechaValidez.getDate() + diasEstadia);
              fechaValidez.setHours(10, 0, 0, 0);
            }

            return (
              <motion.div
                key={venta.id}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                className={`grid grid-cols-12 gap-1 items-center py-2 text-sm rounded-lg transition-colors ${
                  esAnulada
                    ? 'bg-red-900/20 border border-red-500/40 text-red-200'
                    : 'hover:bg-blanco-puro hover:bg-opacity-10'
                }`}
                title={esAnulada ? tooltipAnulacion : undefined}
              >
                <div className="col-span-1 px-1 font-semibold">#{venta.id}</div>
                <div className="col-span-1 px-1">
                  {venta.createdAt ? format(new Date(venta.createdAt), 'HH:mm') : '--:--'}
                </div>
                <div className="col-span-2 px-1 truncate flex items-center space-x-2">
                  <span>{venta.usuario?.nombre || `ID: ${venta.usuario_id}`}</span>
                  {esAnulada && (
                    <span className="px-2 py-0.5 text-xs uppercase tracking-wide bg-red-500/20 border border-red-500/40 rounded-full">
                      Anulada
                    </span>
                  )}
                </div>
                <div className="col-span-1 px-1 text-center font-medium">
                  {venta.totalClientes}
                </div>
                <div
                  className={`col-span-2 px-1 text-right font-bold ${
                    esAnulada ? 'text-red-300 line-through' : 'text-amarillo-dorado'
                  }`}
                >
                  ${Number(venta.totalVentas).toFixed(2)}
                </div>
                <div className="col-span-2 px-1 truncate">
                  {venta.medioPago}
                  {venta.medioPago2 && ` + ${venta.medioPago2}`}
                </div>
                <div
                  className={`col-span-1 px-1 text-right font-extrabold text-[1.1em] tracking-wide neon-effect-azul bg-gradient-to-r from-[#1B4F72]/30 to-transparent px-2 py-1 rounded-lg border-l-4 border-[#00F7FF]/50 shadow-[inset_0_0_10px_rgba(0,247,255,0.3)] ${
                    esAnulada ? 'line-through text-red-200 border-red-400/40 shadow-none' : 'text-[#00F7FF]'
                  }`}
                >
                  ${venta.monto_medio_pago_1?.toFixed(2) || '0.00'}
                </div>
                <div
                  className={`col-span-1 px-1 text-right font-extrabold text-[1.1em] tracking-wide neon-effect-verde bg-gradient-to-l from-[#27AE60]/30 to-transparent px-2 py-1 rounded-lg border-r-4 border-[#00FF88]/50 shadow-[inset_0_0_10px_rgba(0,255,136,0.3)] ${
                    esAnulada ? 'line-through text-red-200 border-red-400/40 shadow-none' : 'text-[#00FF88]'
                  }`}
                >
                  ${venta.monto_medio_pago_2?.toFixed(2) || '0.00'}
                </div>
                <div className="col-span-1 px-1 flex space-x-1 items-center">
                  <button
                    onClick={() => handleImprimirEntrada(venta)}
                    className={`p-1 ${
                      esAnulada ? 'text-red-300 cursor-not-allowed opacity-50' : 'text-azul-claro hover:text-azul-cielo'
                    }`}
                    title={esAnulada ? tooltipAnulacion : 'Imprimir'}
                    disabled={esAnulada}
                  >
                    <Printer size={18} />
                  </button>
                  {esAnulada ? (
                    <Ban size={18} className="text-red-400" title={tooltipAnulacion} />
                  ) : (
                    <button
                      onClick={() => handleAnularVenta(venta.id)}
                      className="p-1 text-rojo-pasion hover:text-red-600"
                      title="Anular"
                    >
                      <Trash2 size={18} />
                    </button>
                  )}
                </div>
                <div className="col-span-12 px-1 text-xs text-gris-piedra mt-1 space-y-1">
                  <div>
                    Estadía: {diasEstadia} día(s)
                    {fechaValidez && ` · Válido hasta: ${format(fechaValidez, 'dd/MM/yyyy HH:mm')}`}
                  </div>
                  {diasDetalle.length > 0 && (
                    <div>
                      Días: {diasDetalle.map((dia) => format(new Date(`${dia}T00:00:00`), 'dd/MM/yyyy')).join(', ')}
                    </div>
                  )}
                  {extrasVenta.length > 0 && (
                    <div>
                      Extras: {extrasTexto} (+${extrasSubtotal.toFixed(2)})
                    </div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>

      <div className="flex justify-between items-center pt-4 border-t border-gris-piedra border-opacity-30">
        <div className="text-lg text-gris-piedra">
          {ventasFiltradas.length} registros
        </div>
        <div className="text-xl font-bold text-amarillo-dorado">
          Total: ${totalActivas.toFixed(2)}
        </div>
      </div>
    </div>
  );
};

export default ListaVentas;
